import type { Board, GeometryElement } from 'jsxgraph';
import type { ActionResult, TransitionProfile } from '../../../model/widget-action.model';
import { parseParamPath } from '../../../util/param-path';
import { computeBoundingBoxForTargets, resolveTarget } from '../util/target-resolver';
import type { JsxgraphThemeService } from '../jsxgraph-theme.service';
import type { AnimationResult, ParameterizedElement } from './action.model';
import { animateValue } from './action-executor';
import { scaleBoundingBox } from './animation';
import { StyleResolverRegistry } from './style';

/**
 * Interface for JSXGraph widgets that can execute built-in actions.
 * JsxgraphBaseWidget implements this interface, allowing actions to receive
 * the widget instance directly instead of a context bag.
 */
export interface BuiltInActionContext {
  readonly board: Board | null;
  readonly objects: Map<string, GeometryElement>;
  readonly parameterizedElements: Map<string, ParameterizedElement>;
  readonly isDestroyed: boolean;
  readonly themeService: JsxgraphThemeService;
  getRegionBoundingBox(region: string | number): [number, number, number, number] | null;
  animateBoundingBox(bbox: [number, number, number, number], transition?: TransitionProfile): Promise<void>;
  /** Called after each animation step in UPDATE_PARAM. Subclasses can override for per-frame reactions. */
  onParamAnimationStep?(): void;
}

/**
 * Focus on a region by animating to its bounding box.
 *
 * Supports multiple region formats:
 * - String element path: "f-roots" → auto-computes bounding box from element objects
 * - Explicit bounding box array: [-5, 5, 5, -5]
 * - Object with type:
 *   - { type: 'element', id: 'f-roots' } → focus on element
 *   - { type: 'point', x: 0, y: -4, radius: 3 } → focus on point with radius
 *   - { type: 'rect', left: -5, top: 5, right: 5, bottom: -5 } → explicit rect
 * - Named region (widget-defined via getRegionBoundingBox override)
 */
export async function focusRegion(
  ctx: BuiltInActionContext,
  payload?: Record<string, unknown>,
  transition?: TransitionProfile,
): Promise<ActionResult> {
  const region = payload?.['region'];
  const padding = (payload?.['padding'] as number) ?? 2;

  if (region === undefined || region === null) {
    return { success: false, error: 'Missing region in payload' };
  }
  if (!ctx.board) {
    return { success: false, error: 'Board not initialized' };
  }

  let bbox: [number, number, number, number] | null = null;

  // 1. Explicit bounding box array
  if (Array.isArray(region) && region.length === 4) {
    bbox = region as [number, number, number, number];
  }
  // 2. String path → try as element target, then as named region
  else if (typeof region === 'string') {
    // First try as element target path
    const resolution = resolveTarget(region, ctx.objects);
    if (resolution.objects.length > 0) {
      bbox = computeBoundingBoxForTargets(resolution.objects, padding);
    }
    // Fall back to widget-defined named regions
    if (!bbox) {
      bbox = ctx.getRegionBoundingBox(region);
    }
  }
  // 3. Object with explicit type
  else if (typeof region === 'object' && region !== null) {
    const regionObj = region as Record<string, unknown>;

    if (regionObj['type'] === 'element') {
      const resolution = resolveTarget(regionObj['id'] as string, ctx.objects);
      bbox = computeBoundingBoxForTargets(resolution.objects, padding);
    } else if (regionObj['type'] === 'point') {
      const x = regionObj['x'] as number;
      const y = regionObj['y'] as number;
      const r = (regionObj['radius'] as number) ?? padding;
      bbox = [x - r, y + r, x + r, y - r];
    } else if (regionObj['type'] === 'rect') {
      bbox = [
        regionObj['left'] as number,
        regionObj['top'] as number,
        regionObj['right'] as number,
        regionObj['bottom'] as number,
      ];
    }
  }

  if (!bbox) {
    return { success: false, error: `Could not resolve region: ${JSON.stringify(region)}` };
  }

  await ctx.animateBoundingBox(bbox, transition);
  return { success: true };
}

export async function transformView(
  ctx: BuiltInActionContext,
  payload?: Record<string, unknown>,
  transition?: TransitionProfile,
): Promise<ActionResult> {
  const mode = payload?.['mode'] as string;
  const factor = (payload?.['factor'] as number) ?? 1.1;

  if (!ctx.board) {
    return { success: false, error: 'Board not initialized' };
  }

  const currentBbox = ctx.board.getBoundingBox();
  let newBbox: [number, number, number, number];

  switch (mode) {
    case 'zoom_in':
      newBbox = scaleBoundingBox(currentBbox, 1 / factor);
      break;
    case 'zoom_out':
      newBbox = scaleBoundingBox(currentBbox, factor);
      break;
    default:
      return { success: false, error: `Unknown transform mode '${mode}'` };
  }

  await ctx.animateBoundingBox(newBbox, transition);
  return { success: true };
}

/**
 * Patch display styles on target element(s) using theme-aware resolution.
 *
 * Target supports element path resolution:
 * - "f"           → All objects for element "f"
 * - "f.0"         → Single object f-0
 * - "f-roots"     → All root point objects
 * - "f-roots.1"   → Single object f-roots-1
 *
 * Style keys are resolved through StyleResolverRegistry:
 * - curve: { color, strokeWidth, dash, visible, opacity }
 * - point: { color, strokeColor, size, visible, opacity }
 * - line: { color, strokeWidth, dash, visible }
 * - polygon: { color, fillColor, strokeColor, strokeWidth, opacity, visible }
 * - label: { color, fontSize, visible }
 * - angle: { color, strokeColor, radius, visible }
 * - circle: { color, fillColor, strokeColor, strokeWidth, opacity, visible }
 */
export function patchDisplay(ctx: BuiltInActionContext, payload?: Record<string, unknown>): ActionResult {
  if (!payload) {
    return { success: false, error: 'Missing payload' };
  }

  // Detect format: if payload has a 'target' key, use single-target mode.
  // Otherwise, treat as batch mode where each top-level key is an elementId.
  if ('target' in payload) {
    return patchDisplaySingle(ctx, payload['target'] as string, payload);
  }

  // Batch mode: { "f": { curve: { color: "green" } }, "lbl": { label: { text: "..." } } }
  let anySuccess = false;
  for (const [elementId, styles] of Object.entries(payload)) {
    if (typeof styles === 'object' && styles !== null) {
      const result = patchDisplaySingle(ctx, elementId, styles as Record<string, unknown>);
      if (result.success) {
        anySuccess = true;
      }
    }
  }

  return anySuccess ? { success: true } : { success: false, error: 'No elements patched' };
}

function patchDisplaySingle(
  ctx: BuiltInActionContext,
  targetPath: string,
  styles: Record<string, unknown>,
): ActionResult {
  if (!targetPath) {
    return { success: false, error: 'Missing target in payload' };
  }

  // Resolve target to objects
  const resolution = resolveTarget(targetPath, ctx.objects);

  if (resolution.objects.length === 0) {
    return { success: false, error: `No objects found for target '${targetPath}'` };
  }

  // Collect all resolved attributes
  const attrs: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(styles)) {
    if (key === 'target') {
      continue;
    }

    // Skip 'label' — handled separately via reconfigure() below
    if (key === 'label') {
      continue;
    }

    // Handle top-level simple attributes
    if (key === 'visible') {
      attrs['visible'] = value;
      continue;
    }

    // Look up resolver for this style key
    const resolver = StyleResolverRegistry.get(key);
    if (resolver && typeof value === 'object' && value !== null) {
      const resolved = resolver.resolve(value as Record<string, unknown>, ctx.themeService);
      Object.assign(attrs, resolved);
    }
  }

  // Handle label config updates via reconfigure — text, color, variant, offset live in the
  // label's config (not as JSXGraph attributes). reconfigure() recomputes derived styles and
  // triggers a board update.
  const labelStyles = styles['label'];
  if (typeof labelStyles === 'object' && labelStyles !== null) {
    for (const obj of resolution.objects) {
      const reconfigure = (obj as unknown as { reconfigure?: (u: Record<string, unknown>) => void }).reconfigure;
      if (reconfigure) {
        reconfigure(labelStyles as Record<string, unknown>);
      }
    }
  }

  // Apply resolved JSXGraph attributes to all matching objects
  if (Object.keys(attrs).length > 0) {
    for (const obj of resolution.objects) {
      (obj as unknown as { setAttribute: (a: Record<string, unknown>) => void }).setAttribute(attrs);
    }
  }

  return { success: true };
}

/**
 * Temporarily highlight elements with theme-aware styles, then restore.
 *
 * Uses the same style resolver system as PATCH_DISPLAY.
 * After the duration, restores original styles.
 *
 * @example
 * // Highlight roots with green color for 1 second
 * {
 *   "action": "HIGHLIGHT",
 *   "payload": {
 *     "target": "f-roots",
 *     "point": { "color": "green", "size": 12 }
 *   },
 *   "transition": { "duration": 1000 }
 * }
 */
export async function highlight(
  ctx: BuiltInActionContext,
  payload?: Record<string, unknown>,
  transition?: TransitionProfile,
): Promise<ActionResult> {
  const targetPath = payload?.['target'] as string;
  if (!targetPath) {
    return { success: false, error: 'Missing target in payload' };
  }

  const duration = transition?.duration ?? 500;

  // Resolve target to objects
  const resolution = resolveTarget(targetPath, ctx.objects);
  if (resolution.objects.length === 0) {
    return { success: false, error: `No objects found for target '${targetPath}'` };
  }

  // Collect highlight styles from payload using style resolvers
  const highlightAttrs: Record<string, unknown> = {};
  if (payload) {
    for (const [key, value] of Object.entries(payload)) {
      if (key === 'target') {
        continue;
      }
      if (key === 'visible') {
        highlightAttrs['visible'] = value;
        continue;
      }
      const resolver = StyleResolverRegistry.get(key);
      if (resolver && typeof value === 'object' && value !== null) {
        Object.assign(highlightAttrs, resolver.resolve(value as Record<string, unknown>, ctx.themeService));
      }
    }
  }

  // If no highlight styles specified, use a default effect (thicker stroke)
  if (Object.keys(highlightAttrs).length === 0) {
    highlightAttrs['strokeWidth'] = 4;
  }

  // Capture original styles for the properties we're changing
  const originalStyles: Map<GeometryElement, Record<string, unknown>> = new Map();
  const styleKeys = Object.keys(highlightAttrs);

  for (const obj of resolution.objects) {
    const original: Record<string, unknown> = {};
    for (const key of styleKeys) {
      // Access the visProp object where JSXGraph stores visual properties
      const visProp = (obj as unknown as { visProp?: Record<string, unknown> }).visProp;
      if (visProp) {
        original[key] = visProp[key.toLowerCase()];
      }
    }
    originalStyles.set(obj, original);

    // Apply highlight styles
    (obj as unknown as { setAttribute: (a: Record<string, unknown>) => void }).setAttribute(highlightAttrs);
  }

  // Wait for duration
  await new Promise((resolve) => setTimeout(resolve, duration));

  // Restore original styles
  for (const obj of resolution.objects) {
    const original = originalStyles.get(obj);
    if (original && Object.keys(original).length > 0) {
      (obj as unknown as { setAttribute: (a: Record<string, unknown>) => void }).setAttribute(original);
    }
  }

  return { success: true };
}

export async function resetView(
  ctx: BuiltInActionContext,
  defaultBbox: [number, number, number, number],
  transition?: TransitionProfile,
): Promise<ActionResult> {
  if (!ctx.board) {
    return { success: false, error: 'Board not initialized' };
  }
  await ctx.animateBoundingBox(defaultBbox, transition);
  return { success: true };
}

/**
 * Update a parameter on a parameterized element with animation.
 *
 * Supports two payload formats:
 * 1. Path-based (new): { param: 'f.a', value: 5 } - parses 'f.a' into element 'f', param 'a'
 * 2. Legacy: { element: 'f', param: 'a', value: 5 } - explicit element and param
 */
export async function updateParam(
  ctx: BuiltInActionContext,
  payload?: Record<string, unknown>,
  transition?: TransitionProfile,
): Promise<ActionResult> {
  if (!ctx.board) {
    return { success: false, error: 'Board not initialized' };
  }

  if (!payload || !('param' in payload) || !('value' in payload)) {
    return { success: false, error: 'Missing param or value in payload' };
  }

  const value = payload['value'] as number;

  // Support both path-based and legacy formats
  let elementId: string;
  let paramName: string;

  if ('element' in payload) {
    // Legacy format: { element: 'f', param: 'a', value: 5 }
    elementId = payload['element'] as string;
    paramName = payload['param'] as string;
  } else {
    // Path-based format: { param: 'f.a', value: 5 }
    const parsed = parseParamPath(payload['param'] as string);
    if (!parsed.root) {
      return {
        success: false,
        error: `Invalid param path '${String(payload['param'])}': must include element (e.g., 'f.a')`,
      };
    }
    elementId = parsed.root;
    paramName = parsed.leaf;
  }

  const element = ctx.parameterizedElements.get(elementId);
  if (!element) {
    return { success: false, error: `Parameterized element '${elementId}' not found` };
  }

  const currentParams = element.getParams();
  const startValue = currentParams[paramName] ?? 0;

  const result: AnimationResult = await animateValue({
    startValue,
    endValue: value,
    transition: transition ?? { duration: 0, easing: 'linear', type: 'none' },
    onStep: (interpolated) => {
      element.setParams({ [paramName]: interpolated });
      ctx.onParamAnimationStep?.();
    },
    shouldAbort: () => ctx.isDestroyed || !ctx.board,
  });

  if (result.aborted) {
    return { success: false, error: 'Animation aborted' };
  }

  return { success: true };
}
